/**
 * Planning Page — Media Plans List
 *
 * Purpose: Primary navigation hub for media plans. Displays all plans the
 *          current user has access to in a metadata-driven MDTable with
 *          search, sort, filter, currency toggle, delete, and create actions.
 * Architecture: Uses SkeletonPage + MDTable (metadata mode) following the
 *               established table page pattern. Columns auto-generated from
 *               the plan_list metadata view (view_type='table').
 * Dependencies: MDTable, SkeletonPage, usePlanList, PlanSummaryArea,
 *               DeleteConfirmationDialog, NotificationSnackbar, planningService
 * Author: Development Team
 * Created: 2026-04-01
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid-pro';
import SkeletonPage from '../../components/layout/SkeletonPage';
import MDTable from '../../components/md-table';
import { DeleteConfirmationDialog } from '../../components/common/DeleteConfirmationDialog';
import { NotificationSnackbar } from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { PermissionKey } from '../../constants/permissionKeys';
import { formatAuditTimestamp, formatAuditUser } from '../../utils/formatting';
import { usePlanList } from './hooks/usePlanList';
import PlanSummaryArea from './components/PlanSummaryArea';
import planningService, { MediaPlan } from '../../services/planningService';

/**
 * PlanningPage Component
 *
 * Purpose: Renders the full Plan List page with summary area, MDTable,
 *          search/currency toolbar, delete dialog, and notifications.
 */
const PlanningPage: React.FC = () => {
  const { state, can } = useAuth();
  const canCreatePlan = can(PermissionKey.MEDIAPLAN_CREATE);
  // Rows are selectable only to delete them
  const canDeletePlan = can(PermissionKey.MEDIAPLAN_DELETE);
  const tenantId = state.user?.activeTenant?.tenant_id || 'GLOBAL';
  const navigate = useNavigate();

  // Plan data from hook
  const { plans, loading, error, searchQuery, setSearchQuery, refresh } = usePlanList(tenantId);

  // Currency toggle
  const [currencyMode, setCurrencyMode] = useState<'local' | 'global'>('local');

  // Selection state
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Notification state
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // --- Handlers ---

  const handleRowClick = useCallback(
    (row: MediaPlan) => {
      navigate(`/planning/${row.id}`);
    },
    [navigate]
  );

  const handleSelectionChange = useCallback((ids: string[]) => {
    setSelectedIds(ids);
  }, []);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    [setSearchQuery]
  );

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, [setSearchQuery]);

  const handleCurrencyToggle = useCallback(
    (_: React.MouseEvent<HTMLElement>, newMode: 'local' | 'global' | null) => {
      if (newMode !== null) {
        setCurrencyMode(newMode);
      }
    },
    []
  );

  const handleDeleteClick = useCallback(() => {
    if (selectedIds.length > 0) {
      setDeleteDialogOpen(true);
    }
  }, [selectedIds]);

  const handleConfirmDelete = useCallback(async () => {
    setDeleteLoading(true);
    try {
      for (const id of selectedIds) {
        await planningService.deletePlan(id);
      }
      setDeleteDialogOpen(false);
      setSelectedIds([]);
      setNotification({
        open: true,
        message:
          selectedIds.length === 1
            ? 'Plan deleted successfully'
            : `${selectedIds.length} plans deleted successfully`,
        severity: 'success',
      });
      refresh();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      let message: string;
      if (typeof detail === 'string') {
        message = detail;
      } else if (detail && typeof detail === 'object' && 'message' in detail) {
        message = detail.message;
      } else {
        message = err.message || 'Failed to delete plan';
      }
      setNotification({ open: true, message, severity: 'error' });
    } finally {
      setDeleteLoading(false);
    }
  }, [selectedIds, refresh]);

  const handleCloseDeleteDialog = useCallback(() => {
    if (!deleteLoading) {
      setDeleteDialogOpen(false);
    }
  }, [deleteLoading]);

  // --- Column configuration ---

  // Appended after metadata-generated columns
  const columnsAfterRelatedFields: GridColDef[] = useMemo(
    () => [
      {
        field: 'updated_at',
        headerName: 'Last Updated',
        width: 180,
        sortable: true,
        filterable: false,
        resizable: true,
        renderCell: (params: any) => {
          const updatedAt = params.row?.updated_at;
          const updatedBy = params.row?.updated_by;
          if (!updatedAt) return '-';
          return (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                lineHeight: 1.3,
                py: 0.5,
              }}
            >
              <Typography variant="body2" noWrap>
                {formatAuditUser(updatedBy)}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {formatAuditTimestamp(updatedAt)}
              </Typography>
            </Box>
          );
        },
      },
    ],
    []
  );

  // --- Toolbar configuration ---

  const customToolbarFilters = useMemo(
    () => (
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search plans..."
          value={searchQuery}
          onChange={handleSearchChange}
          data-testid="plan-search-input"
          sx={{ minWidth: 200 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch} aria-label="Clear search">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
        />
        <ToggleButtonGroup
          value={currencyMode}
          exclusive
          onChange={handleCurrencyToggle}
          size="small"
          aria-label="Currency display mode"
        >
          <ToggleButton value="local" data-testid="currency-local-btn">
            Local
          </ToggleButton>
          <Tooltip title="Coming Soon - requires exchange rate service">
            <span>
              <ToggleButton value="global" disabled data-testid="currency-global-btn">
                Global
              </ToggleButton>
            </span>
          </Tooltip>
        </ToggleButtonGroup>
      </Box>
    ),
    [searchQuery, handleSearchChange, handleClearSearch, currencyMode, handleCurrencyToggle]
  );

  const customToolbarActions = useMemo(
    () =>
      canCreatePlan && (
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => navigate('/planning/create')}
          data-testid="create-plan-btn"
        >
          Create New Plan
        </Button>
      ),
    [navigate, canCreatePlan]
  );

  // Header actions — shows delete button when plans are selected
  const customHeaderActions = useMemo(() => {
    if (!canDeletePlan || selectedIds.length === 0) return null;
    return (
      <Tooltip title={`Delete ${selectedIds.length} selected plan(s)`}>
        <IconButton
          size="small"
          color="error"
          onClick={handleDeleteClick}
          data-testid="delete-selected-btn"
          aria-label="Delete selected plans"
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    );
  }, [canDeletePlan, selectedIds, handleDeleteClick]);

  // Get the plan name for the delete dialog
  const deleteItemName = useMemo(() => {
    if (selectedIds.length === 1) {
      const plan = plans.find((p) => p.id === selectedIds[0]);
      return plan?.['fd_plan-name'] || plan?.id || '';
    }
    return `${selectedIds.length} plans`;
  }, [selectedIds, plans]);

  // --- Render ---

  return (
    <SkeletonPage pageTitle="Media Plans" breadcrumbs={[{ label: 'Planning', href: '/planning' }]}>
      <Box data-testid="planning-page">
        <PlanSummaryArea />

        <MDTable
          mode="metadata"
          metadata={{
            modelId: 'mediaplan',
            viewType: 'table',
            tenantId,
            columnsAfterRelatedFields,
          }}
          data={plans}
          loading={loading}
          error={error}
          tenantId={tenantId}
          onRowClick={handleRowClick}
          onSelectionChange={handleSelectionChange}
          onRefresh={refresh}
          showRefreshButton={true}
          showAddButton={false}
          selectable={canDeletePlan}
          // Only plans the server would let this user delete (ORCH-332 `_authz`)
          isRowSelectable={(plan) => plan._authz?.can_delete !== false}
          sortable={true}
          filterable={true}
          defaultSortModel={[{ field: 'fd_plan-name', sort: 'asc' }]}
          customToolbarFilters={customToolbarFilters}
          customToolbarActions={customToolbarActions}
          customHeaderActions={customHeaderActions}
        />

        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          onConfirm={handleConfirmDelete}
          itemName={deleteItemName}
          itemType="media plan"
          loading={deleteLoading}
          warningMessage="This will permanently delete the selected media plan(s). This action cannot be undone."
        />

        <NotificationSnackbar
          open={notification.open}
          onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
          message={notification.message}
          severity={notification.severity}
          autoHideDuration={6000}
        />
      </Box>
    </SkeletonPage>
  );
};

export default PlanningPage;
